// src/store/expenses/expensesSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiService from '../../services/api.service';

export const fetchExpenses = createAsyncThunk(
  'expenses/fetchExpenses',
  async (filters = {}, { rejectWithValue }) => {
    try {
      const response = await apiService.getExpenses(filters);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

export const fetchExpenseCategories = createAsyncThunk(
  'expenses/fetchExpenseCategories',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiService.getExpenseCategories();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

export const createExpense = createAsyncThunk(
  'expenses/createExpense',
  async (expense, { rejectWithValue }) => {
    try {
      const response = await apiService.createExpense(expense);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

export const updateExpense = createAsyncThunk(
  'expenses/updateExpense',
  async ({ id, expense }, { rejectWithValue }) => {
    try {
      const response = await apiService.updateExpense(id, expense);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

export const deleteExpense = createAsyncThunk(
  'expenses/deleteExpense',
  async (id, { rejectWithValue }) => {
    try {
      await apiService.deleteExpense(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

export const createExpenseCategory = createAsyncThunk(
  'expenses/createExpenseCategory',
  async (category, { rejectWithValue }) => {
    try {
      const response = await apiService.createExpenseCategory(category);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

export const updateExpenseCategory = createAsyncThunk(
  'expenses/updateExpenseCategory',
  async ({ id, category }, { rejectWithValue }) => {
    try {
      const response = await apiService.updateExpenseCategory(id, category);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

export const deleteExpenseCategory = createAsyncThunk(
  'expenses/deleteExpenseCategory',
  async (id, { rejectWithValue }) => {
    try {
      await apiService.deleteExpenseCategory(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

export const reassignExpenses = createAsyncThunk(
  'expenses/reassignExpenses',
  async ({ sourceId, targetId }, { rejectWithValue }) => {
    try {
      const response = await apiService.reassignExpenses(sourceId, targetId);
      return { sourceId, targetId, message: response.data.detail };
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

export const fetchExpenseSummary = createAsyncThunk(
  'expenses/fetchExpenseSummary',
  async (groupBy = 'category', { rejectWithValue }) => {
    try {
      const response = await apiService.getExpenseSummary(groupBy);
      return { data: response.data, groupBy };
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

const initialState = {
  expenses: [],
  categories: [],
  summary: {
    byCategory: [],
    byMonth: [],
    byProject: []
  },
  currentExpense: null,
  isLoading: false,
  error: null,
  successMessage: null
};

const expensesSlice = createSlice({
  name: 'expenses',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },
    clearCurrentExpense: (state) => {
      state.currentExpense = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // fetchExpenses
      .addCase(fetchExpenses.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchExpenses.fulfilled, (state, action) => {
        state.isLoading = false;
        state.expenses = action.payload;
      })
      .addCase(fetchExpenses.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      
      // fetchExpenseCategories
      .addCase(fetchExpenseCategories.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchExpenseCategories.fulfilled, (state, action) => {
        state.isLoading = false;
        state.categories = action.payload;
      })
      .addCase(fetchExpenseCategories.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      
      // createExpense
      .addCase(createExpense.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createExpense.fulfilled, (state, action) => {
        state.isLoading = false;
        state.expenses.push(action.payload);
        state.successMessage = 'Expense created successfully';
      })
      .addCase(createExpense.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      
      // updateExpense
      .addCase(updateExpense.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateExpense.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.expenses.findIndex(expense => expense.id === action.payload.id);
        if (index !== -1) {
          state.expenses[index] = action.payload;
        }
        state.successMessage = 'Expense updated successfully';
      })
      .addCase(updateExpense.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      
      // deleteExpense
      .addCase(deleteExpense.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteExpense.fulfilled, (state, action) => {
        state.isLoading = false;
        state.expenses = state.expenses.filter(expense => expense.id !== action.payload);
        state.successMessage = 'Expense deleted successfully';
      })
      .addCase(deleteExpense.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      
      // createExpenseCategory
      .addCase(createExpenseCategory.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createExpenseCategory.fulfilled, (state, action) => {
        state.isLoading = false;
        state.categories.push(action.payload);
        state.successMessage = 'Category created successfully';
      })
      .addCase(createExpenseCategory.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      
      // updateExpenseCategory
      .addCase(updateExpenseCategory.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateExpenseCategory.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.categories.findIndex(category => category.id === action.payload.id);
        if (index !== -1) {
          state.categories[index] = action.payload;
        }
        state.successMessage = 'Category updated successfully';
      })
      .addCase(updateExpenseCategory.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      
      // deleteExpenseCategory
      .addCase(deleteExpenseCategory.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteExpenseCategory.fulfilled, (state, action) => {
        state.isLoading = false;
        state.categories = state.categories.filter(category => category.id !== action.payload);
        state.successMessage = 'Category deleted successfully';
      })
      .addCase(deleteExpenseCategory.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      
      // reassignExpenses
      .addCase(reassignExpenses.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(reassignExpenses.fulfilled, (state, action) => {
        state.isLoading = false;
        state.successMessage = action.payload.message;
      })
      .addCase(reassignExpenses.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      
      // fetchExpenseSummary
      .addCase(fetchExpenseSummary.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchExpenseSummary.fulfilled, (state, action) => {
        state.isLoading = false;
        const { data, groupBy } = action.payload;
        
        switch (groupBy) {
          case 'category':
            state.summary.byCategory = data;
            break;
          case 'month':
            state.summary.byMonth = data;
            break;
          case 'project':
            state.summary.byProject = data;
            break;
          default:
            break;
        }
      })
      .addCase(fetchExpenseSummary.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  }
});

export const { clearError, clearSuccessMessage, clearCurrentExpense } = expensesSlice.actions;
export default expensesSlice.reducer;